import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { normalizeCampaignId } from '@/utils/budgetRuleCampaigns';
import {
  CAMPAIGN_SEARCH_PLACEHOLDER,
  filterCampaignsBySearch,
} from '@/utils/campaignSearch';
import type { SpCampaignNormalized } from '@/types/spCampaigns';

type CampaignSearchPickerProps = {
  options: SpCampaignNormalized[];
  value: string;
  onChange: (campaignId: string) => void;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  emptyMessage?: string;
  loadingMessage?: string;
};

export function CampaignSearchPicker({
  options,
  value,
  onChange,
  disabled = false,
  loading = false,
  placeholder = CAMPAIGN_SEARCH_PLACEHOLDER,
  emptyMessage = 'No campaigns match your search',
  loadingMessage = 'Loading campaigns…',
}: CampaignSearchPickerProps) {
  const listId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selected = useMemo(
    () => options.find((c) => normalizeCampaignId(c.campaignId) === normalizeCampaignId(value)),
    [options, value]
  );

  const filtered = useMemo(
    () => filterCampaignsBySearch(options, query),
    [options, query]
  );

  const inputDisabled = disabled || loading;
  const showList = open && !inputDisabled;

  const inputValue =
    open || !value
      ? query
      : selected
        ? `${selected.name} (${selected.campaignId})`
        : query;

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (value && !options.some((c) => normalizeCampaignId(c.campaignId) === normalizeCampaignId(value))) {
      onChange('');
    }
  }, [value, options, onChange]);

  const handleFocus = () => {
    if (inputDisabled) return;
    setOpen(true);
    if (value && selected) {
      setQuery(selected.name);
      onChange('');
    }
  };

  const handleInputChange = (next: string) => {
    setQuery(next);
    onChange('');
    setOpen(true);
  };

  const handleSelect = (campaignId: string) => {
    onChange(campaignId);
    setQuery('');
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setQuery('');
    setOpen(true);
  };

  const listEmptyLabel = loading
    ? loadingMessage
    : options.length === 0
      ? 'No campaigns available to link'
      : emptyMessage;

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          type="text"
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleFocus}
          placeholder={loading ? loadingMessage : placeholder}
          disabled={inputDisabled}
          className="w-full border border-border rounded-lg px-2.5 py-1.5 pr-8 text-sm disabled:bg-page disabled:text-foreground-subtle"
        />
        {value && !inputDisabled ? (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-chrome-label hover:text-foreground-muted text-sm leading-none"
            aria-label="Clear selection"
          >
            ×
          </button>
        ) : null}
      </div>

      {showList ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto rounded-xl border border-border bg-surface shadow-lg list-none p-0 m-0"
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-sm text-foreground-subtle">{listEmptyLabel}</li>
          ) : (
            filtered.map((c) => {
              const isSelected =
                normalizeCampaignId(c.campaignId) === normalizeCampaignId(value);
              return (
                <li key={c.campaignId} role="option" aria-selected={isSelected}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelect(c.campaignId)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-brand-subtle ${
                      isSelected ? 'bg-brand-subtle font-medium' : ''
                    }`}
                  >
                    <span className="block font-medium text-foreground truncate">{c.name}</span>
                    <span className="block text-xs text-foreground-subtle font-mono truncate">
                      {c.campaignId}
                    </span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      ) : null}
    </div>
  );
}
